// Venue achievement definitions and tracking system
export const VENUE_ACHIEVEMENTS = {
  // Founder's Club - First 50 venues
  VENUE_FOUNDERS_CLUB: {
    id: 'venue_founders_club',
    name: "Venue Founder's Club",
    description: 'One of the first 50 venues to join Trivia Plug',
    icon: '🏢',
    category: 'exclusive',
    requirement: 'Be among the first 50 venues to register'
  },

  // Founder Badge - Special for you
  VENUE_FOUNDER: {
    id: 'venue_founder',
    name: 'Venue Founder',
    description: 'The creator of Trivia Plug venue system',
    icon: '🌟',
    category: 'exclusive',
    requirement: 'Platform founder'
  },

  // Company Founder Badge - Most prestigious
  COMPANY_FOUNDER: {
    id: 'company_founder',
    name: 'Company Founder',
    description: 'The founder and CEO of Trivia Plug',
    icon: '👑',
    category: 'exclusive',
    requirement: 'Company founder'
  },

  // Game Hosting Milestones
  VENUE_ROOKIE: {
    id: 'venue_rookie',
    name: 'Venue Rookie',
    description: 'Host your first 10 games',
    icon: '🎮',
    category: 'milestone',
    requirement: 'Games hosted >= 10'
  },
  VENUE_REGULAR: {
    id: 'venue_regular',
    name: 'Regular Venue',
    description: 'Host 50 games',
    icon: '🎯',
    category: 'milestone',
    requirement: 'Games hosted >= 50'
  },
  VENUE_VETERAN: {
    id: 'venue_veteran',
    name: 'Veteran Venue',
    description: 'Host 100 games',
    icon: '🎖️',
    category: 'milestone',
    requirement: 'Games hosted >= 100'
  },
  VENUE_LEGEND: {
    id: 'venue_legend',
    name: 'Venue Legend',
    description: 'Host 500 games',
    icon: '🏅',
    category: 'milestone',
    requirement: 'Games hosted >= 500'
  },

  // Team Engagement Achievements
  TEAM_MAGNET: {
    id: 'team_magnet',
    name: 'Team Magnet',
    description: 'Host games with 100+ different teams',
    icon: '🧲',
    category: 'engagement',
    requirement: 'Host 100+ different teams'
  },
  TEAM_HUB: {
    id: 'team_hub',
    name: 'Team Hub',
    description: 'Host games with 500+ different teams',
    icon: '🏘️',
    category: 'engagement',
    requirement: 'Host 500+ different teams'
  },

  // Revenue Achievements
  REVENUE_STARTER: {
    id: 'revenue_starter',
    name: 'Revenue Starter',
    description: 'Generate $1,000 in revenue',
    icon: '💰',
    category: 'revenue',
    requirement: 'Generate $1,000+ in revenue'
  },
  REVENUE_MASTER: {
    id: 'revenue_master',
    name: 'Revenue Master',
    description: 'Generate $10,000 in revenue',
    icon: '💎',
    category: 'revenue',
    requirement: 'Generate $10,000+ in revenue'
  },
  REVENUE_LEGEND: {
    id: 'revenue_legend',
    name: 'Revenue Legend',
    description: 'Generate $50,000 in revenue',
    icon: '👑',
    category: 'revenue',
    requirement: 'Generate $50,000+ in revenue'
  },

  // Performance Achievements
  HIGH_PERFORMANCE: {
    id: 'high_performance',
    name: 'High Performance',
    description: 'Average 4.0+ team GPA across all games',
    icon: '⭐',
    category: 'performance',
    requirement: 'Average team GPA >= 4.0 (5.0 scale)'
  },
  EXCELLENCE: {
    id: 'excellence',
    name: 'Excellence',
    description: 'Average 4.5+ team GPA with 50+ games',
    icon: '🌟',
    category: 'performance',
    requirement: 'Average team GPA >= 4.5 AND games >= 50'
  },
  PERFECTION: {
    id: 'perfection',
    name: 'Perfection',
    description: 'Average 5.0 team GPA with 100+ games',
    icon: '👑',
    category: 'performance',
    requirement: 'Average team GPA >= 5.0 AND games >= 100'
  },

  // Host Partnership Achievements
  HOST_PARTNER: {
    id: 'host_partner',
    name: 'Host Partner',
    description: 'Work with 10 different hosts',
    icon: '🤝',
    category: 'partnership',
    requirement: 'Work with 10+ different hosts'
  },
  HOST_HUB: {
    id: 'host_hub',
    name: 'Host Hub',
    description: 'Work with 25 different hosts',
    icon: '🏢',
    category: 'partnership',
    requirement: 'Work with 25+ different hosts'
  },

  // Consistency Achievements
  CONSISTENT_VENUE: {
    id: 'consistent_venue',
    name: 'Consistent Venue',
    description: 'Host games for 90 consecutive days',
    icon: '📅',
    category: 'consistency',
    requirement: 'Host for 90+ consecutive days'
  },
  DEDICATED_VENUE: {
    id: 'dedicated_venue',
    name: 'Dedicated Venue',
    description: 'Host games for 365 consecutive days',
    icon: '💪',
    category: 'consistency',
    requirement: 'Host for 365+ consecutive days'
  },

  // Premium Features Achievements
  PREMIUM_VENUE: {
    id: 'premium_venue',
    name: 'Premium Venue',
    description: 'Activate all premium features',
    icon: '💎',
    category: 'premium',
    requirement: 'Activate all premium features'
  },
  FEATURE_MASTER: {
    id: 'feature_master',
    name: 'Feature Master',
    description: 'Use all available venue features',
    icon: '🎛️',
    category: 'premium',
    requirement: 'Use all available features'
  }
};

// Check if venue qualifies for Founder's Club (first 50 venues)
export const checkVenueFoundersClub = async (venueId, db) => {
  try {
    const { collection, query, orderBy, limit, getDocs } = await import('firebase/firestore');
    
    // Get all venues ordered by creation date
    const venuesQuery = query(
      collection(db, 'venues'),
      orderBy('createdAt', 'asc'),
      limit(50)
    );
    const venuesSnapshot = await getDocs(venuesQuery);
    
    // Check if this venue is in the first 50
    const isFounder = venuesSnapshot.docs.some(doc => doc.id === venueId);
    return isFounder;
  } catch (error) {
    console.error('Error checking Venue Founder\'s Club:', error);
    return false;
  }
};

// Check if user is the venue founder
export const checkVenueFounder = (userId) => {
  const FOUNDER_USER_ID = '25ASAtsXhQYFIR3wOX2m2TTaJxD3';
  return userId === FOUNDER_USER_ID;
};

// Check if user is the company founder (CEO) - Venue side
export const checkCompanyFounder = (userId, userEmail) => {
  const VENUE_CEO_EMAIL = 'patrickbrianmoore@outlook.com';
  return userEmail === VENUE_CEO_EMAIL;
};

// Check and award achievements based on venue data
export const checkVenueAchievements = (venueData) => {
  const newAchievements = [];
  const currentAchievements = venueData.achievements || [];

  // Game count achievements
  if (venueData.totalGames >= 10 && !currentAchievements.includes('venue_rookie')) {
    newAchievements.push('venue_rookie');
  }
  if (venueData.totalGames >= 50 && !currentAchievements.includes('venue_regular')) {
    newAchievements.push('venue_regular');
  }
  if (venueData.totalGames >= 100 && !currentAchievements.includes('venue_veteran')) {
    newAchievements.push('venue_veteran');
  }
  if (venueData.totalGames >= 500 && !currentAchievements.includes('venue_legend')) {
    newAchievements.push('venue_legend');
  }

  // Team engagement achievements
  if (venueData.uniqueTeams >= 100 && !currentAchievements.includes('team_magnet')) {
    newAchievements.push('team_magnet');
  }
  if (venueData.uniqueTeams >= 500 && !currentAchievements.includes('team_hub')) {
    newAchievements.push('team_hub');
  }

  // Revenue achievements
  if (venueData.totalRevenue >= 1000 && !currentAchievements.includes('revenue_starter')) {
    newAchievements.push('revenue_starter');
  }
  if (venueData.totalRevenue >= 10000 && !currentAchievements.includes('revenue_master')) {
    newAchievements.push('revenue_master');
  }
  if (venueData.totalRevenue >= 50000 && !currentAchievements.includes('revenue_legend')) {
    newAchievements.push('revenue_legend');
  }

  // Performance achievements
  if (venueData.averageGPAScore >= 4.0 && !currentAchievements.includes('high_performance')) {
    newAchievements.push('high_performance');
  }
  if (venueData.averageGPAScore >= 4.5 && venueData.totalGames >= 50 && !currentAchievements.includes('excellence')) {
    newAchievements.push('excellence');
  }
  if (venueData.averageGPAScore >= 5.0 && venueData.totalGames >= 100 && !currentAchievements.includes('perfection')) {
    newAchievements.push('perfection');
  }

  // Host partnership achievements
  if (venueData.uniqueHosts >= 10 && !currentAchievements.includes('host_partner')) {
    newAchievements.push('host_partner');
  }
  if (venueData.uniqueHosts >= 25 && !currentAchievements.includes('host_hub')) {
    newAchievements.push('host_hub');
  }

  // Consistency achievements
  if (venueData.consecutiveDays >= 90 && !currentAchievements.includes('consistent_venue')) {
    newAchievements.push('consistent_venue');
  }
  if (venueData.consecutiveDays >= 365 && !currentAchievements.includes('dedicated_venue')) {
    newAchievements.push('dedicated_venue');
  }

  // Premium features achievements
  const premiumFeatures = [
    venueData.premiumLeagueSlots,
    venueData.exclusiveAds,
    venueData.customSlides,
    venueData.prioritySupport
  ];
  const activeFeatures = premiumFeatures.filter(Boolean).length;
  
  if (activeFeatures >= 4 && !currentAchievements.includes('premium_venue')) {
    newAchievements.push('premium_venue');
  }
  if (activeFeatures >= 4 && !currentAchievements.includes('feature_master')) {
    newAchievements.push('feature_master');
  }

  return newAchievements;
};

// Get achievement display info
export const getVenueAchievementInfo = (achievementId) => {
  return VENUE_ACHIEVEMENTS[achievementId.toUpperCase()] || null;
};

// Get all achievements by category
export const getVenueAchievementsByCategory = () => {
  const categories = {};
  
  Object.values(VENUE_ACHIEVEMENTS).forEach(achievement => {
    if (!categories[achievement.category]) {
      categories[achievement.category] = [];
    }
    categories[achievement.category].push(achievement);
  });
  
  return categories;
};

// Utility function to manually award company founder badge
export const awardCompanyFounderBadgeToVenue = async (venueId, db) => {
  try {
    const { doc, getDoc, updateDoc } = await import('firebase/firestore');
    
    // Get current venue data
    const venueDoc = await getDoc(doc(db, 'venues', venueId));
    if (!venueDoc.exists()) {
      throw new Error('Venue not found');
    }
    
    const venueData = venueDoc.data();
    const currentAchievements = venueData.achievements || [];
    
    // Check if already has the badge
    if (currentAchievements.includes('company_founder')) {
      console.log('Venue already has company founder badge');
      return false;
    }
    
    // Add the badge
    const updatedAchievements = [...currentAchievements, 'company_founder'];
    await updateDoc(doc(db, 'venues', venueId), {
      achievements: updatedAchievements
    });
    
    console.log('Company founder badge awarded to venue successfully');
    return true;
  } catch (error) {
    console.error('Error awarding company founder badge to venue:', error);
    return false;
  }
};

// Utility function to manually award company founder badge by email
export const awardCompanyFounderBadgeToVenueByEmail = async (venueEmail, db) => {
  try {
    const { collection, query, where, getDocs, updateDoc, doc } = await import('firebase/firestore');
    
    // Find venue by email
    const venuesQuery = query(collection(db, 'venues'), where('email', '==', venueEmail));
    const venuesSnapshot = await getDocs(venuesQuery);
    
    if (venuesSnapshot.empty) {
      throw new Error('Venue not found with that email');
    }
    
    const venueDoc = venuesSnapshot.docs[0];
    const venueData = venueDoc.data();
    const currentAchievements = venueData.achievements || [];
    
    // Check if already has the badge
    if (currentAchievements.includes('company_founder')) {
      console.log('Venue already has company founder badge');
      return false;
    }
    
    // Add the badge
    const updatedAchievements = [...currentAchievements, 'company_founder'];
    await updateDoc(doc(db, 'venues', venueDoc.id), {
      achievements: updatedAchievements
    });
    
    console.log('Company founder badge awarded to venue successfully for', venueEmail);
    return true;
  } catch (error) {
    console.error('Error awarding company founder badge to venue:', error);
    return false;
  }
}; 